import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { supabase } from "./supabaseClient";
import { useAuth } from "./AuthContext";

/**
 * aUEC inflation tracker — pulled live from UEX Corp's Currency Index API.
 *
 *   GET /2.0/currencies_index?currency=UEC          → current snapshot + basket composition
 *   GET /2.0/currencies_index_history?currency=UEC  → historical IDX values for trend
 *
 * The currencies_index endpoints are public (no auth required), but we proxy
 * them through the `uex-currency` Supabase Edge Function so the call stays on
 * a known origin, CORS is controlled, and an optional UEX_API_KEY can be
 * attached server-side for higher rate limits.
 *
 * The Laspeyres index uses Dec 2023 as the base period (= 100). A value of
 * ~640 means the same commodity basket now costs ~6.4× as many aUEC as it
 * did at launch — i.e. aUEC has lost ~84% of its purchasing power.
 */

const EDGE_URL = `${process.env.REACT_APP_SUPABASE_URL || "https://negtawswkqbvvwobozdh.supabase.co"}/functions/v1/uex-currency`;
const REFRESH_MS = 5 * 60 * 1000;

export default function UexCurrencyWidget() {
  const { profile } = useAuth();
  const isSuperAdmin = profile?.perm_super_admin === true || profile?.admin_tier === "super_admin";
  const [snapshot, setSnapshot] = useState(null);
  const [history, setHistory] = useState(null);
  const [error, setError] = useState(null);
  const [trace, setTrace] = useState(null);
  const [hasKey, setHasKey] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updatedAt, setUpdatedAt] = useState(null);
  const timerRef = useRef(null);

  // Merit peg state. Read from app_settings; super-admins can edit via modal.
  const [meritPeg, setMeritPeg] = useState({ auec: null, setAtIdx: null, setAt: null });
  const [pegEditOpen, setPegEditOpen] = useState(false);

  const fetchMeritPeg = useCallback(async () => {
    try {
      const { data, error: err } = await supabase
        .from("app_settings")
        .select("key, value")
        .in("key", ["merit_value_auec", "merit_value_set_at_idx", "merit_value_set_at"]);
      if (err) return;
      const m = Object.fromEntries((data || []).map(r => [r.key, r.value]));
      const auec = parseFloat(m.merit_value_auec);
      const setAtIdx = parseFloat(m.merit_value_set_at_idx);
      setMeritPeg({
        auec: Number.isFinite(auec) && auec > 0 ? auec : null,
        setAtIdx: Number.isFinite(setAtIdx) && setAtIdx > 0 ? setAtIdx : null,
        setAt: m.merit_value_set_at || null,
      });
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { fetchMeritPeg(); }, [fetchMeritPeg]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const res = await fetch(EDGE_URL, {
        headers: {
          Accept: "application/json",
          ...(session?.access_token ? { Authorization: `Bearer ${session.access_token}` } : {}),
        },
      });
      let env = null;
      try { env = await res.json(); } catch { /* */ }
      if (Array.isArray(env?.trace)) setTrace(env.trace); else setTrace(null);
      if (typeof env?.has_key === "boolean") setHasKey(env.has_key); else setHasKey(null);
      if (!res.ok || !env?.ok) {
        throw new Error(env?.error || `HTTP ${res.status}`);
      }
      setSnapshot(extractSnapshot(env.snapshot));
      setHistory(extractHistory(env.history));
      setUpdatedAt(new Date());
    } catch (e) {
      setError(e?.message || String(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    timerRef.current = setInterval(fetchData, REFRESH_MS);
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [fetchData]);

  const components = useMemo(() => {
    if (!snapshot?.components) return [];
    return [...snapshot.components].sort((a, b) => (Number(b.weight) || 0) - (Number(a.weight) || 0));
  }, [snapshot]);

  const idxChange = snapshot ? Number(snapshot.index_value) - 100 : null;
  const idxChangePct = snapshot ? ((Number(snapshot.index_value) - 100) / 100) * 100 : null;
  // Purchasing power = how much the same basket buys today vs base period.
  // If the basket costs 6.4× more aUEC now, 1 aUEC buys 1/6.4 = ~15.6% of
  // what it used to → a loss of ~84.4%. This is the inverse of basket cost.
  const purchasingPowerPct = snapshot && Number(snapshot.index_value) > 0
    ? (100 / Number(snapshot.index_value) - 1) * 100
    : null;

  return (
    <div style={{
      padding: 20,
      border: "1px solid rgba(252,214,87,0.25)",
      background: "linear-gradient(135deg, rgba(252,214,87,0.04) 0%, rgba(252,214,87,0.01) 100%)",
      marginBottom: 32,
    }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, marginBottom: 16, flexWrap: "wrap" }}>
        <div>
          <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, letterSpacing: 3, color: "#fcd657", textTransform: "uppercase" }}>
            aUEC Inflation · UEX Corp Index
          </div>
          <div style={{ fontFamily: "'Orbitron', sans-serif", fontWeight: 700, fontSize: 16, color: "#e8eaf6", marginTop: 4 }}>
            Star Citizen Currency Tracker
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          {updatedAt && (
            <span style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, letterSpacing: 1.2, color: "rgba(232,234,246,0.45)" }}>
              Updated {updatedAt.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
            </span>
          )}
          <button onClick={fetchData} type="button" disabled={loading}
            style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, letterSpacing: 1.5, padding: "5px 10px", background: "transparent", border: "1px solid rgba(252,214,87,0.4)", color: "#fcd657", textTransform: "uppercase", cursor: loading ? "wait" : "pointer", opacity: loading ? 0.6 : 1 }}>
            {loading ? "Refreshing…" : "↻ Refresh"}
          </button>
          <a href="https://uexcorp.space/currencies/home/currency/UEC/" target="_blank" rel="noreferrer"
            style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, letterSpacing: 1.5, padding: "5px 10px", background: "transparent", border: "1px solid rgba(255,255,255,0.15)", color: "rgba(232,234,246,0.55)", textTransform: "uppercase", textDecoration: "none" }}>
            View on UEX ↗
          </a>
        </div>
      </div>

      {error ? (
        <div style={{ padding: "12px 14px", border: "1px solid rgba(239,83,80,0.3)", background: "rgba(239,83,80,0.05)" }}>
          <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, letterSpacing: 1.5, color: "#ef5350", textTransform: "uppercase", marginBottom: 4 }}>
            UEX API unreachable
          </div>
          <div style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: 13, color: "rgba(232,234,246,0.55)" }}>
            {error}
          </div>
          {hasKey !== null && (
            <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, letterSpacing: 1.2, color: hasKey ? "#34d399" : "rgba(232,234,246,0.45)", textTransform: "uppercase", marginTop: 8 }}>
              Edge function {hasKey ? "has UEX_API_KEY" : "running without UEX_API_KEY (key is optional)"}
            </div>
          )}
          {Array.isArray(trace) && trace.length > 0 && (
            <details style={{ marginTop: 8 }}>
              <summary style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, letterSpacing: 1.5, color: "rgba(232,234,246,0.55)", cursor: "pointer", textTransform: "uppercase" }}>
                Show endpoint trace ({trace.length})
              </summary>
              <pre style={{ marginTop: 8, padding: 10, background: "rgba(0,0,0,0.4)", border: "1px solid rgba(255,255,255,0.08)", color: "rgba(232,234,246,0.75)", fontFamily: "'Space Mono', monospace", fontSize: 10, lineHeight: 1.5, maxHeight: 220, overflow: "auto", whiteSpace: "pre-wrap" }}>
{trace.map((t, i) => `${i + 1}. [${t.auth || "?"}] ${t.url}\n   → HTTP ${t.status}${t.error ? "  ERR " + t.error : ""}${t.sample ? "\n   sample: " + t.sample.slice(0, 160) : ""}`).join("\n\n")}
              </pre>
            </details>
          )}
        </div>
      ) : !snapshot && loading ? (
        <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 10, letterSpacing: 1.5, color: "rgba(232,234,246,0.4)", textTransform: "uppercase", textAlign: "center", padding: 16 }}>
          Pulling latest snapshot…
        </div>
      ) : snapshot ? (
        <>
          {/* Headline row: IDX value, change vs base, basket value, sparkline */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 12, marginBottom: 16 }}>
            <HeadlineCard
              label="UEC IDX"
              value={fmtIdx(snapshot.index_value)}
              hint={`Base ${formatBasePeriod(snapshot)} = 100`}
              accent="rgba(252,214,87,0.45)"
              fg="#fcd657"
            />
            <HeadlineCard
              label="Basket Cost vs Base"
              value={idxChange != null ? `${idxChange > 0 ? "+" : ""}${idxChange.toFixed(2)}` : "—"}
              hint={idxChangePct != null ? `${idxChangePct > 0 ? "+" : ""}${idxChangePct.toFixed(1)}% more expensive` : null}
              accent={idxChange > 0 ? "rgba(239,83,80,0.4)" : "rgba(52,211,153,0.4)"}
              fg={idxChange > 0 ? "#ef5350" : "#34d399"}
            />
            <HeadlineCard
              label="Purchasing Power"
              value={purchasingPowerPct != null ? `${purchasingPowerPct > 0 ? "+" : ""}${purchasingPowerPct.toFixed(1)}%` : "—"}
              hint="1 aUEC vs Dec 2023"
              accent={purchasingPowerPct != null && purchasingPowerPct < 0 ? "rgba(239,83,80,0.4)" : "rgba(52,211,153,0.4)"}
              fg={purchasingPowerPct != null && purchasingPowerPct < 0 ? "#ef5350" : "#34d399"}
            />
            <HeadlineCard
              label="Basket Value"
              value={fmtNum(snapshot.basket_value, { maxDigits: 0 }) + " aUEC"}
              hint={`Σ(weight × avg price)`}
            />
            <HistorySparkline history={history} currentValue={snapshot.index_value} />
          </div>

          {/* Merit value row — fixed peg + inflation-adjusted */}
          <MeritValueRow
            peg={meritPeg}
            currentIdx={snapshot.index_value}
            isSuperAdmin={isSuperAdmin}
            onEdit={() => setPegEditOpen(true)}
          />

          {/* Basket Composition table */}
          <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, letterSpacing: 2.5, color: "rgba(232,234,246,0.45)", textTransform: "uppercase", marginBottom: 8 }}>
            Basket Composition · {components.length} commodities
          </div>
          <div style={{ overflowX: "auto", border: "1px solid rgba(255,255,255,0.06)" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontFamily: "'Rajdhani', sans-serif", fontSize: 13 }}>
              <thead>
                <tr style={{ background: "rgba(255,255,255,0.02)" }}>
                  <Th>Commodity</Th>
                  <Th align="right">Weight</Th>
                  <Th align="right">Base Price</Th>
                  <Th align="right">Avg Price (window)</Th>
                  <Th align="right">Price Chg.</Th>
                  <Th align="right" hideOnMobile>Data Pts</Th>
                  <Th align="right">Contribution</Th>
                </tr>
              </thead>
              <tbody>
                {components.map(c => {
                  const base = Number(c.base_price) || 0;
                  const avg = Number(c.avg_price) || 0;
                  const chgPct = base > 0 ? ((avg - base) / base) * 100 : null;
                  const chgColor = chgPct == null ? "rgba(232,234,246,0.6)" : chgPct >= 0 ? "#ef5350" : "#34d399";
                  return (
                    <tr key={c.id_commodity || c.commodity_slug || c.commodity_name}
                        style={{ borderTop: "1px solid rgba(255,255,255,0.04)" }}>
                      <Td>{c.commodity_name || c.commodity_code || c.commodity_slug || "—"}</Td>
                      <Td align="right">{(Number(c.weight) * 100).toFixed(1)}%</Td>
                      <Td align="right">{fmtNum(c.base_price, { maxDigits: 0 })}</Td>
                      <Td align="right" color="#34d399">{fmtNum(c.avg_price, { maxDigits: 0 })}</Td>
                      <Td align="right" color={chgColor}>
                        {chgPct == null ? "—" : `${chgPct >= 0 ? "+" : ""}${chgPct.toFixed(1)}%`}
                      </Td>
                      <Td align="right" hideOnMobile color="rgba(232,234,246,0.5)">{c.data_points ?? "—"}</Td>
                      <Td align="right">{fmtNum(c.contribution, { maxDigits: 2 })}</Td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr style={{ borderTop: "1px solid rgba(255,255,255,0.1)", background: "rgba(252,214,87,0.04)" }}>
                  <Td colSpan={6} color="rgba(232,234,246,0.55)" size={11}>
                    Methodology: {snapshot.methodology || "laspeyres_dynamic_v1"} · window {snapshot.data_window_days || "—"}d · UEX {formatDate(snapshot.date_modified)}
                  </Td>
                  <Td align="right"><strong style={{ color: "#fcd657" }}>{fmtIdx(snapshot.index_value)}</strong></Td>
                </tr>
              </tfoot>
            </table>
          </div>
        </>
      ) : (
        <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 10, color: "rgba(232,234,246,0.5)" }}>
          No data.
        </div>
      )}

      {pegEditOpen && (
        <MeritPegEditModal
          currentPeg={meritPeg}
          currentIdx={snapshot?.index_value}
          onClose={() => setPegEditOpen(false)}
          onSaved={() => { fetchMeritPeg(); setPegEditOpen(false); }}
        />
      )}
    </div>
  );
}

function MeritValueRow({ peg, currentIdx, isSuperAdmin, onEdit }) {
  const hasPeg = peg?.auec != null;
  const adjustedAuec = hasPeg && peg.setAtIdx > 0 && currentIdx > 0
    ? peg.auec * (currentIdx / peg.setAtIdx)
    : null;
  const setAtLabel = peg?.setAt ? new Date(peg.setAt).toLocaleDateString() : "—";

  return (
    <div style={{
      display: "grid",
      gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
      gap: 12,
      marginBottom: 16,
      padding: 14,
      border: "1px solid rgba(252,214,87,0.18)",
      background: "rgba(252,214,87,0.03)",
    }}>
      <div style={{ gridColumn: "1 / -1", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
        <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, letterSpacing: 2.5, color: "#fcd657", textTransform: "uppercase" }}>
          Merit Value · Pegged to aUEC
        </div>
        {isSuperAdmin && (
          <button onClick={onEdit} type="button"
            style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, letterSpacing: 1.5, padding: "5px 12px", background: "transparent", border: "1px solid rgba(252,214,87,0.5)", color: "#fcd657", textTransform: "uppercase", cursor: "pointer" }}>
            {hasPeg ? "Edit Peg" : "Set Peg"}
          </button>
        )}
      </div>
      <div style={{ padding: 12, border: "1px solid rgba(252,214,87,0.18)", background: "rgba(255,255,255,0.02)" }}>
        <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 8, letterSpacing: 1.8, color: "rgba(252,214,87,0.55)", textTransform: "uppercase", marginBottom: 6 }}>
          Merit Peg (Fixed)
        </div>
        <div style={{ fontFamily: "'Orbitron', sans-serif", fontWeight: 700, fontSize: 18, color: "#fcd657" }}>
          {hasPeg ? `1 Merit = ${fmtNum(peg.auec, { maxDigits: 0 })} aUEC` : "Not set"}
        </div>
        <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 7, letterSpacing: 1.2, color: "rgba(232,234,246,0.4)", marginTop: 4, textTransform: "uppercase" }}>
          {hasPeg ? `Set ${setAtLabel} when IDX was ${peg.setAtIdx?.toFixed(2) || "—"}` : "Super-admins can set a value"}
        </div>
      </div>
      <div style={{ padding: 12, border: "1px solid rgba(52,211,153,0.25)", background: "rgba(255,255,255,0.02)" }}>
        <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 8, letterSpacing: 1.8, color: "rgba(52,211,153,0.55)", textTransform: "uppercase", marginBottom: 6 }}>
          Merit Adjusted (Today)
        </div>
        <div style={{ fontFamily: "'Orbitron', sans-serif", fontWeight: 700, fontSize: 18, color: "#34d399" }}>
          {adjustedAuec != null ? `1 Merit = ${fmtNum(adjustedAuec, { maxDigits: 0 })} aUEC` : "—"}
        </div>
        <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 7, letterSpacing: 1.2, color: "rgba(232,234,246,0.4)", marginTop: 4, textTransform: "uppercase" }}>
          {adjustedAuec != null && peg?.setAtIdx ? `× current IDX / set-at IDX (${currentIdx?.toFixed(0)} / ${peg.setAtIdx?.toFixed(0)})` : "tracks UEX inflation"}
        </div>
      </div>
    </div>
  );
}

function MeritPegEditModal({ currentPeg, currentIdx, onClose, onSaved }) {
  const [val, setVal] = useState(currentPeg?.auec != null ? String(currentPeg.auec) : "");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);

  const save = async () => {
    const n = Number(val);
    if (!Number.isFinite(n) || n <= 0) { setErr("Enter a positive number"); return; }
    if (!Number.isFinite(Number(currentIdx)) || Number(currentIdx) <= 0) { setErr("UEX IDX unavailable — wait for the snapshot to load"); return; }
    setSaving(true);
    setErr(null);
    try {
      const rows = [
        { key: "merit_value_auec", value: String(n) },
        { key: "merit_value_set_at_idx", value: String(Number(currentIdx)) },
        { key: "merit_value_set_at", value: new Date().toISOString() },
      ];
      const { error } = await supabase.from("app_settings").upsert(rows, { onConflict: "key" });
      if (error) throw error;
      onSaved?.();
    } catch (e) {
      setErr(e?.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.75)", zIndex: 100, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }} onClick={onClose}>
      <div style={{ background: "#0a0d18", border: "1px solid rgba(252,214,87,0.35)", padding: 28, maxWidth: 480, width: "100%" }} onClick={e => e.stopPropagation()}>
        <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 10, letterSpacing: 2.5, color: "#fcd657", textTransform: "uppercase", marginBottom: 4 }}>
          Set Merit Peg
        </div>
        <div style={{ fontFamily: "'Orbitron', sans-serif", fontWeight: 700, fontSize: 18, color: "#e8eaf6", marginBottom: 16 }}>
          1 Merit = ? aUEC
        </div>
        <input type="number" min="0" step="1" autoFocus value={val} onChange={e => setVal(e.target.value)}
          placeholder="e.g. 1000"
          style={{ width: "100%", padding: "10px 12px", fontFamily: "'Rajdhani', sans-serif", fontSize: 18, background: "rgba(255,255,255,0.04)", border: "1px solid rgba(252,214,87,0.3)", color: "#e8eaf6" }} />
        <div style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: 13, color: "rgba(232,234,246,0.55)", marginTop: 12, lineHeight: 1.5 }}>
          The fixed peg never changes on its own — but the widget shows the inflation-adjusted value alongside, computed from the current UEX IDX ({currentIdx?.toFixed(2) || "—"}) vs the IDX at the moment you save this peg.
        </div>
        {err && (
          <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 10, letterSpacing: 1.2, color: "#ef5350", marginTop: 10 }}>{err}</div>
        )}
        <div style={{ display: "flex", gap: 8, marginTop: 18, justifyContent: "flex-end" }}>
          <button onClick={onClose} type="button" disabled={saving}
            style={{ fontFamily: "'Space Mono', monospace", fontSize: 10, letterSpacing: 1.5, padding: "8px 16px", background: "transparent", border: "1px solid rgba(255,255,255,0.2)", color: "rgba(232,234,246,0.7)", textTransform: "uppercase", cursor: saving ? "wait" : "pointer" }}>
            Cancel
          </button>
          <button onClick={save} type="button" disabled={saving}
            style={{ fontFamily: "'Space Mono', monospace", fontSize: 10, letterSpacing: 1.5, padding: "8px 16px", background: "rgba(252,214,87,0.15)", border: "1px solid rgba(252,214,87,0.5)", color: "#fcd657", textTransform: "uppercase", cursor: saving ? "wait" : "pointer", opacity: saving ? 0.6 : 1 }}>
            {saving ? "Saving…" : "Save Peg"}
          </button>
        </div>
      </div>
    </div>
  );
}

function HeadlineCard({ label, value, hint, accent, fg }) {
  return (
    <div style={{ padding: 12, border: `1px solid ${accent || "rgba(252,214,87,0.18)"}`, background: "rgba(255,255,255,0.02)" }}>
      <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 8, letterSpacing: 1.8, color: accent || "rgba(232,234,246,0.45)", textTransform: "uppercase", marginBottom: 6 }}>
        {label}
      </div>
      <div style={{ fontFamily: "'Orbitron', sans-serif", fontWeight: 700, fontSize: 18, color: fg || "#e8eaf6" }}>
        {value}
      </div>
      {hint && (
        <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 7, letterSpacing: 1.2, color: "rgba(232,234,246,0.4)", marginTop: 4, textTransform: "uppercase" }}>
          {hint}
        </div>
      )}
    </div>
  );
}

function HistorySparkline({ history, currentValue }) {
  const series = useMemo(() => {
    if (!Array.isArray(history)) return [];
    return history
      .map(h => ({ t: Number(h.date_added) || 0, v: Number(h.index_value) || 0 }))
      .filter(p => p.t > 0 && p.v > 0)
      .sort((a, b) => a.t - b.t);
  }, [history]);

  if (series.length < 2) {
    return (
      <div style={{ padding: 12, border: "1px solid rgba(252,214,87,0.18)", background: "rgba(255,255,255,0.02)" }}>
        <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 8, letterSpacing: 1.8, color: "rgba(232,234,246,0.45)", textTransform: "uppercase", marginBottom: 6 }}>
          90d Trend
        </div>
        <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 10, color: "rgba(232,234,246,0.4)" }}>
          History pending
        </div>
      </div>
    );
  }

  const w = 160, h = 48;
  const xs = series.map(p => p.t);
  const ys = series.map(p => p.v);
  const xmin = Math.min(...xs), xmax = Math.max(...xs);
  const ymin = Math.min(...ys, currentValue || ys[0]);
  const ymax = Math.max(...ys, currentValue || ys[0]);
  const yspan = Math.max(1, ymax - ymin);
  const points = series.map(p => {
    const x = xmax === xmin ? 0 : ((p.t - xmin) / (xmax - xmin)) * w;
    const y = h - ((p.v - ymin) / yspan) * h;
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  }).join(" ");

  const first = series[0].v;
  const last = series[series.length - 1].v;
  const trendUp = last >= first;
  const trendColor = trendUp ? "#ef5350" : "#34d399";

  return (
    <div style={{ padding: 12, border: "1px solid rgba(252,214,87,0.18)", background: "rgba(255,255,255,0.02)" }}>
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
        <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 8, letterSpacing: 1.8, color: "rgba(232,234,246,0.45)", textTransform: "uppercase" }}>
          90d Trend
        </div>
        <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 8, letterSpacing: 1.2, color: trendColor, textTransform: "uppercase" }}>
          {trendUp ? "▲" : "▼"} {Math.abs(((last - first) / first) * 100).toFixed(1)}%
        </div>
      </div>
      <svg width="100%" height={h} viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" style={{ display: "block" }}>
        <polyline fill="none" stroke={trendColor} strokeWidth="1.5" points={points} />
      </svg>
    </div>
  );
}

function Th({ children, align = "left", hideOnMobile }) {
  return (
    <th style={{
      textAlign: align,
      padding: "8px 10px",
      fontFamily: "'Space Mono', monospace",
      fontSize: 9,
      letterSpacing: 1.5,
      color: "rgba(232,234,246,0.5)",
      textTransform: "uppercase",
      fontWeight: 400,
      whiteSpace: "nowrap",
    }}>
      {children}
    </th>
  );
}

function Td({ children, align = "left", color, size = 13, colSpan }) {
  return (
    <td colSpan={colSpan} style={{
      textAlign: align,
      padding: "7px 10px",
      fontFamily: "'Rajdhani', sans-serif",
      fontSize: size,
      color: color || "rgba(232,234,246,0.85)",
      whiteSpace: "nowrap",
    }}>
      {children}
    </td>
  );
}

function extractSnapshot(raw) {
  if (!raw) return null;
  let inner = raw;
  if (raw && typeof raw === "object" && "data" in raw) inner = raw.data;
  if (Array.isArray(inner)) inner = inner[0] || null;
  if (!inner) return null;
  return {
    id: inner.id,
    currency: inner.currency,
    index_value: Number(inner.index_value) || 0,
    basket_value: Number(inner.basket_value) || 0,
    methodology: inner.methodology,
    data_window_days: inner.data_window_days,
    date_modified: inner.date_modified || inner.date_added,
    components: Array.isArray(inner.components) ? inner.components : [],
  };
}

function extractHistory(raw) {
  if (!raw) return null;
  let inner = raw;
  if (raw && typeof raw === "object" && "data" in raw) inner = raw.data;
  if (!Array.isArray(inner)) return null;
  return inner;
}

function fmtIdx(v) {
  const n = Number(v);
  if (!Number.isFinite(n)) return "—";
  return n.toFixed(2);
}

function fmtNum(v, { maxDigits = 2 } = {}) {
  const n = Number(v);
  if (!Number.isFinite(n)) return "—";
  return n.toLocaleString(undefined, { maximumFractionDigits: maxDigits });
}

function formatBasePeriod() {
  return "Dec 2023";
}

function formatDate(v) {
  try {
    const n = Number(v);
    const d = Number.isFinite(n) && n > 0 ? new Date(n * 1000) : new Date(v);
    if (Number.isNaN(d.getTime())) return String(v);
    return d.toLocaleDateString();
  } catch { return String(v); }
}
